import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 1595 — Tier 3 server specs on a project (vendor-neutral RFQ; no money anywhere).
// Pulse super-admin only during rollout — callers pass `enabled` from useCurrentUser.
export interface ServerSpecRow {
  id: string;
  projectId: string;
  name: string;
  nodeCount: number;
  specJson: string;
  lastModifiedDate: string;
}

export interface ServerSpecSave {
  name: string;
  nodeCount: number;
  specJson: string;
}

export interface ServerSpecConvert {
  unitCost: number;
  unitPrice: number;
  vendorName: string;
  vendorQuoteNumber: string;
  description: string;
}

export const useServerSpecs = (projectId: string, enabled: boolean) => useQuery<ServerSpecRow[]>(
  ['serverSpecs', projectId],
  async () => {
    const { data } = await axios.get(`${apiUrl()}ServerSpecs/ByProject/${projectId}`);
    return Array.isArray(data) ? data : [];
  },
  { enabled: !!projectId && enabled },
);

export const useServerSpecActions = (projectId: string) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries(['serverSpecs', projectId]);
  const create = useMutation(
    (s: ServerSpecSave) => axios.post(`${apiUrl()}ServerSpecs`, { projectId, ...s }),
    { onSuccess: invalidate },
  );
  const update = useMutation(
    ({ id, ...s }: ServerSpecSave & { id: string }) => axios.put(`${apiUrl()}ServerSpecs/${id}`, { projectId, ...s }),
    { onSuccess: invalidate },
  );
  const archive = useMutation(
    (id: string) => axios.delete(`${apiUrl()}ServerSpecs/${id}`),
    { onSuccess: invalidate },
  );
  // item 1595 — spec → Sales Order (vendor pricing hand-typed); returns { workOrderId, number }
  const convert = useMutation(
    ({ id, ...body }: ServerSpecConvert & { id: string }) => axios.post<{ workOrderId: string; number: string }>(`${apiUrl()}ServerSpecs/${id}/convert`, body),
    {
      onSuccess: () => {
        invalidate();
        queryClient.invalidateQueries('workOrders');
        queryClient.invalidateQueries('project');
      },
    },
  );
  return {
    create, update, archive, convert,
  };
};

export default useServerSpecs;
